import { useEffect, useMemo, useState, type ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { apiClient } from "../../api/client"
import { formatDistanceToNow } from "date-fns"

// BEGIN PLATFORM STAGE 1
function FilterButton({
  active,
  children,
  onClick,
}: {
  active: boolean
  children: ReactNode
  onClick: () => void
}) {
  const base = 'px-3 py-1 text-sm font-medium border border-slate-200 focus:outline-none'
  const activeCls = ' bg-primary-600 text-white'
  const inactive = ' bg-white text-slate-700 hover:bg-slate-50'
  return (
    <button type="button" className={`${base}${active ? activeCls : inactive}`} onClick={onClick}>
      {children}
    </button>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
    >
      {children}
    </th>
  )
}

function Td({ children, colSpan, className }: { children: React.ReactNode; colSpan?: number; className?: string }) {
  return (
    <td className={`px-4 py-3 text-sm text-slate-800 ${className ?? ''}`} colSpan={colSpan}>
      {children}
    </td>
  )
}
// END PLATFORM STAGE 1

interface Summary {
  tenants_total: number
  tenants_active_7d: number
  users_total: number
  sessions_7d: number
  notes_locked_7d: number
  exports_7d: number
  errors_7d: number
  api_version?: string
  git_sha?: string
  build_time?: string
  // BEGIN PLATFORM STAGE 4
  exports_ever?: boolean
  last_export_at?: string | null
  cpt_configured?: boolean
  // END PLATFORM STAGE 4
  // BEGIN PLATFORM STAGE 5
  unlocked_notes_over_48h_total?: number
  lock_rate_7d?: number
  missing_signatures_count?: number
  // END PLATFORM STAGE 5
  // BEGIN PLATFORM STAGE 6
  audit_events_7d?: number
  last_admin_action_at?: string | null
  last_platform_action_at?: string | null
  // END PLATFORM STAGE 6
  missing_sources?: string[]
}

interface TenantRow {
  tenant_id: number
  name: string
  slug: string
  domain: string
  is_active: boolean
  created_at: string
  last_activity_at?: string | null
  users_count: number
  // BEGIN PLATFORM STAGE 2
  users_admin_count: number
  users_bcba_count: number
  users_rbt_count: number
  users_other_count: number
  seat_limit?: number | null
  seats_used: number
  // END PLATFORM STAGE 2
  // BEGIN PLATFORM STAGE 3
  documents_count: number
  records_total_estimate: number
  last_30d_activity_score: number
  // END PLATFORM STAGE 3
  clients_count: number
  sessions_7d: number
  notes_locked_7d: number
  billing_exports_7d: number
  error_count_7d: number
  // BEGIN PLATFORM STAGE 4
  exports_ever?: boolean
  last_export_at?: string | null
  cpt_configured?: boolean
  // END PLATFORM STAGE 4
  // BEGIN PLATFORM STAGE 5
  unlocked_notes_over_48h_count?: number
  lock_rate_7d?: number
  missing_signatures_count?: number
  // END PLATFORM STAGE 5
  // BEGIN PLATFORM STAGE 6
  audit_events_7d?: number
  last_admin_action_at?: string | null
  last_platform_action_at?: string | null
  // END PLATFORM STAGE 6
  // BEGIN PLATFORM STAGE 7
  needs_support?: boolean
  internal_notes?: string | null
  tags?: string[]
  // END PLATFORM STAGE 7
  // BEGIN PLATFORM STAGE 8
  feature_flags?: Record<string, boolean>
  // END PLATFORM STAGE 8
  // BEGIN PLATFORM STAGE 9
  api_version?: string | null
  git_sha?: string | null
  build_time?: string | null
  // END PLATFORM STAGE 9
}

export default function PlatformDashboardPage() {
  const navigate = useNavigate()
  const [summary, setSummary] = useState<Summary | null>(null)
  const [tenants, setTenants] = useState<TenantRow[]>([])
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const [summaryRes, tenantsRes] = await Promise.all([
          apiClient.get('/api/platform/summary/'),
          apiClient.get('/api/platform/tenants/'),
        ])
        setSummary(summaryRes.data)
        setTenants(tenantsRes.data.tenants || [])
      } catch (e) {
        setError('Unable to load platform metrics')
      } finally {
        setLoading(false)
      }
    }

    void load()
  }, [])

  const filteredTenants = useMemo(() => {
    return tenants
      .filter((t) =>
        statusFilter === 'all' ? true : statusFilter === 'active' ? t.is_active : !t.is_active
      )
      .filter((t) => {
        const term = search.toLowerCase()
        if (!term) return true
        return (
          t.name.toLowerCase().includes(term) ||
          (t.slug || '').toLowerCase().includes(term) ||
          (t.domain || '').toLowerCase().includes(term)
        )
      })
      .sort((a, b) => (b.last_activity_at || '').localeCompare(a.last_activity_at || ''))
  }, [tenants, statusFilter, search])

  if (loading) {
    return <div className="p-8 text-gray-600">Loading platform metrics�</div>
  }

  if (error) {
    return <div className="p-8 text-red-600">{error}</div>
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b bg-white">
        <div className="mx-auto max-w-7xl px-6 py-6 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-semibold text-slate-900">Platform Owner Dashboard</h1>
            <div className="text-sm text-slate-500">
              Read-only � No PHI
              {summary?.api_version && (
                <span className="ml-2">
                  API {summary.api_version}{summary.git_sha ? ` � ${summary.git_sha.slice(0,7)}` : ''}
                </span>
              )}
              {summary?.build_time && <span className="ml-2">Built {summary.build_time}</span>}
            </div>
            </div>
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Tenants" value={summary.tenants_total} />
              <StatCard label="Active (7d)" value={summary.tenants_active_7d} />
              <StatCard label="Users" value={summary.users_total} />
              <StatCard label="Sessions (7d)" value={summary.sessions_7d} />
              <StatCard label="Locked Notes (7d)" value={summary.notes_locked_7d} />
              <StatCard label="Exports (7d)" value={summary.exports_7d} />
              <StatCard label="Errors (7d)" value={summary.errors_7d} intent="warning" />
              {/* BEGIN PLATFORM STAGE 4 */}
              <StatCard
                label="Billing readiness"
                value={summary.cpt_configured ? 'CPT ready' : 'Not configured'}
              />
              <StatCard
                label="Exports ever"
                value={summary.exports_ever ? 'Yes' : 'No'}
              />
              {/* Compliance signals */}
              <StatCard
                label="Unlocked >48h"
                value={summary.unlocked_notes_over_48h_total ?? 0}
                intent="warning"
              />
              <StatCard
                label="Lock rate (7d)"
                value={`${Math.round((summary.lock_rate_7d ?? 0) * 100)}%`}
              />
              <StatCard
                label="Audit events (7d)"
                value={summary.audit_events_7d ?? 0}
              />
              <StatCard
                label="Last admin action"
                value={summary.last_admin_action_at ? new Date(summary.last_admin_action_at).toLocaleString() : 'n/a'}
              />
              {/* END PLATFORM STAGE 4 */}
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-6">
        <div className="flex flex-wrap gap-3 mb-4 items-center">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <FilterButton active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>
              All
            </FilterButton>
            <FilterButton active={statusFilter === 'active'} onClick={() => setStatusFilter('active')}>
              Active
            </FilterButton>
            <FilterButton active={statusFilter === 'inactive'} onClick={() => setStatusFilter('inactive')}>
              Inactive
            </FilterButton>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, slug, domain"
            className="input w-full md:w-64"
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <Th>Name</Th>
                <Th>Slug</Th>
                <Th>Domain</Th>
                <Th>Users / Seats</Th>
                <Th>Usage (30d)</Th>
                <Th>Clients</Th>
                <Th>Sessions (7d)</Th>
                <Th>Errors (7d)</Th>
                <Th>Compliance</Th>
                <Th>Audit</Th>
                <Th>Support</Th>
                <Th>Last activity</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {filteredTenants.map((tenant) => (
                <tr
                  key={tenant.tenant_id}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/platform/tenants/${tenant.tenant_id}`)}
                >
                  <Td>
                    <div className="font-medium text-slate-900">{tenant.name}</div>
                    <div className="text-xs text-slate-500">{tenant.is_active ? 'Active' : 'Inactive'}</div>
                  </Td>
                  <Td>{tenant.slug || '�'}</Td>
                  <Td>{tenant.domain || '�'}</Td>
                  <Td>
                    <div className="font-semibold text-slate-900">
                      {tenant.seats_used}
                      {tenant.seat_limit ? ` / ${tenant.seat_limit}` : ' seats'}
                    </div>
                    <div className="text-xs text-slate-500 space-x-2">
                      <span>Admin {tenant.users_admin_count}</span>
                      <span>BCBA {tenant.users_bcba_count}</span>
                      <span>RBT {tenant.users_rbt_count}</span>
                      <span>Other {tenant.users_other_count}</span>
                    </div>
                  </Td>
                  <Td>
                    <div className="font-semibold text-slate-900">{tenant.records_total_estimate}</div>
                    <div className="text-xs text-slate-500">
                      Docs {tenant.documents_count ?? 0} � Activity {tenant.last_30d_activity_score}
                    </div>
                  </Td>
                  <Td>{tenant.clients_count}</Td>
                  <Td>{tenant.sessions_7d}</Td>
                  <Td className={tenant.error_count_7d > 0 ? 'text-red-600' : ''}>{tenant.error_count_7d}</Td>
                  <Td>
                    <div className="text-sm text-slate-800">
                      Unlock &gt;48h: {tenant.unlocked_notes_over_48h_count ?? 0}
                    </div>
                    <div className="text-xs text-slate-500">
                      Lock rate 7d: {Math.round((tenant.lock_rate_7d ?? 0) * 100)}%
                    </div>
                  </Td>
                  <Td>
                    <div className="text-sm text-slate-800">Events 7d: {tenant.audit_events_7d ?? 0}</div>
                    <div className="text-xs text-slate-500">
                      Last admin: {tenant.last_admin_action_at ? new Date(tenant.last_admin_action_at).toLocaleString() : 'n/a'}
                    </div>
                  </Td>
                  <Td>
                    <div className="text-sm text-slate-800">
                      {tenant.needs_support ? 'Needs support' : 'Normal'}
                    </div>
                    {tenant.tags && tenant.tags.length > 0 && (
                      <div className="text-xs text-slate-500">Tags: {tenant.tags.join(', ')}</div>
                    )}
                  </Td>
                  <Td>
                    {tenant.last_activity_at
                      ? formatDistanceToNow(new Date(tenant.last_activity_at), { addSuffix: true })
                      : 'n/a'}
                  </Td>
                </tr>
              ))}
              {filteredTenants.length === 0 && (
                <tr>
                  <Td colSpan={12}>
                    <div className="text-sm text-slate-500">No tenants match your filters.</div>
                  </Td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// BEGIN PLATFORM STAGE 1
function StatCard({
  label,
  value,
  intent = 'default',
}: {
  label: string
  value: number | string
  intent?: 'default' | 'warning'
}) {
  const color = intent === 'warning' ? 'text-amber-700 bg-amber-50' : 'text-slate-900'
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-sm text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  )
}
// END PLATFORM STAGE 1






